import React, { useState, useEffect } from 'react';
import { 
  Users, Activity, Clock, MousePointer, Eye, TrendingUp, 
  Monitor, Globe, Smartphone, MapPin, BarChart3, PieChart,
  Zap, ArrowLeft, RefreshCw, Download, AlertCircle, Calendar,
  TrendingDown, Filter, Moon, Sun, Target, Percent
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { format, subDays, subHours, startOfDay, endOfDay } from 'date-fns';

const Admin = ({ onBack }) => {
  const [stats, setStats] = useState(null);
  const [liveSessions, setLiveSessions] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [timeRange, setTimeRange] = useState('today');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [darkMode, setDarkMode] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [trafficAlerts, setTrafficAlerts] = useState([]);

  useEffect(() => {
    fetchAnalytics();
    
    if (autoRefresh) {
      const interval = setInterval(fetchAnalytics, 5000);
      return () => clearInterval(interval);
    }
  }, [timeRange, autoRefresh]);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`https://rar.sillydev.lol/api/analytics/stats?range=${timeRange}`);
      const data = await response.json();
      setStats(data.stats);
      setLiveSessions(data.liveSessions);
      setRecentEvents(data.recentEvents);
      setChartData(data.chartData);
      setComparison(data.comparison);
      setTrafficAlerts(data.alerts || []);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const exportData = async (format = 'json') => {
    try {
      const response = await fetch(`https://rar.sillydev.lol/api/analytics/export?range=${timeRange}&format=${format}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_${timeRange}_${Date.now()}.${format}`;
      a.click();
    } catch (error) {
      console.error('Failed to export data:', error);
    }
  };

  const COLORS = ['#edc58f', '#dba965', '#cab297', '#b8945f', '#a67c3a'];

  if (!stats) {
    return (
      <div className="min-h-screen bg-espresso-950 flex items-center justify-center">
        <div className="text-center">
          <Activity className="w-12 h-12 text-coffee-400 mx-auto mb-4 animate-pulse" />
          <p className="text-cream-200">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-espresso-950 py-8 px-4">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 rounded-lg bg-glass-dark hover:bg-glass-light transition-all"
            >
              <ArrowLeft className="w-5 h-5 text-cream-100" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-cream-100">Analytics Dashboard</h1>
              <p className="text-coffee-300 text-sm">Real-time insights and visitor analytics</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            {/* Time Range Selector */}
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 rounded-lg bg-glass-dark text-cream-100 border border-glass-border text-sm"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="all">All Time</option>
            </select>

            {/* Auto Refresh Toggle */}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`p-2 rounded-lg transition-all ${
                autoRefresh ? 'bg-green-600 text-white' : 'bg-glass-dark text-coffee-300'
              }`}
              title="Auto Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${autoRefresh ? 'animate-spin' : ''}`} />
            </button>

            {/* Export Dropdown */}
            <div className="relative group">
              <button className="px-4 py-2 rounded-lg bg-coffee-600 text-cream-100 hover:bg-coffee-500 transition-all flex items-center gap-2">
                <Download className="w-4 h-4" />
                Export
              </button>
              <div className="absolute right-0 mt-2 w-32 bg-glass-dark border border-glass-border rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                <button onClick={() => exportData('json')} className="w-full px-4 py-2 text-left text-cream-200 hover:bg-glass-light text-sm">JSON</button>
                <button onClick={() => exportData('csv')} className="w-full px-4 py-2 text-left text-cream-200 hover:bg-glass-light text-sm">CSV</button>
              </div>
            </div>
          </div>
        </div>

        {/* Traffic Alerts */}
        {trafficAlerts.length > 0 && (
          <div className="mb-6 space-y-2">
            {trafficAlerts.map((alert, idx) => (
              <div key={idx} className="bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0" />
                <p className="text-cream-200 text-sm">{alert.message}</p>
              </div>
            ))}
          </div>
        )}

        {/* Key Metrics with Comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            icon={<Zap className="w-6 h-6" />}
            title="Live Visitors"
            value={liveSessions.length}
            subtitle={`${stats.totalVisitors} total ${timeRange}`}
            color="green"
            pulse
          />
          <MetricCard
            icon={<Eye className="w-6 h-6" />}
            title="Page Views"
            value={stats.totalPageViews}
            change={stats.pageViewsChange}
            comparison={comparison?.pageViews}
            color="blue"
          />
          <MetricCard
            icon={<Clock className="w-6 h-6" />}
            title="Avg. Session"
            value={formatDuration(stats.avgTimeOnSite)}
            change={stats.timeChange}
            comparison={comparison?.avgTime}
            color="purple"
          />
          <MetricCard
            icon={<Percent className="w-6 h-6" />}
            title="Bounce Rate"
            value={`${stats.bounceRate || 0}%`}
            change={stats.bounceRateChange}
            comparison={comparison?.bounceRate}
            color="orange"
            inverse
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Traffic Over Time */}
          <div className="bg-glass-dark rounded-2xl p-6 border border-glass-border">
            <h2 className="text-xl font-semibold text-cream-100 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Traffic Over Time
            </h2>
            {chartData?.traffic && (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={chartData.traffic}>
                  <defs>
                    <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#edc58f" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#edc58f" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                  <XAxis dataKey="time" stroke="#cab297" style={{ fontSize: '12px' }} />
                  <YAxis stroke="#cab297" style={{ fontSize: '12px' }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1612', border: '1px solid #cab29750', borderRadius: '8px' }}
                    labelStyle={{ color: '#ffe8c8' }}
                  />
                  <Area type="monotone" dataKey="visitors" stroke="#edc58f" fillOpacity={1} fill="url(#colorVisitors)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Device & Browser Pie Charts */}
          <div className="bg-glass-dark rounded-2xl p-6 border border-glass-border">
            <h2 className="text-xl font-semibold text-cream-100 mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5" />
              Device & Browser Split
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {chartData?.devices && (
                <div>
                  <p className="text-xs text-coffee-400 mb-2 text-center">Devices</p>
                  <ResponsiveContainer width="100%" height={180}>
                    <RechartsPie>
                      <Pie
                        data={chartData.devices}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                        label={(entry) => `${entry.percentage}%`}
                      >
                        {chartData.devices.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1612', border: '1px solid #cab29750', borderRadius: '8px' }}
                      />
                    </RechartsPie>
                  </ResponsiveContainer>
                  <div className="flex flex-col gap-1 mt-2">
                    {chartData.devices.map((device, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx] }}></div>
                        <span className="text-coffee-300">{device.name}: {device.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {chartData?.browsers && (
                <div>
                  <p className="text-xs text-coffee-400 mb-2 text-center">Browsers</p>
                  <ResponsiveContainer width="100%" height={180}>
                    <RechartsPie>
                      <Pie
                        data={chartData.browsers}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                        label={(entry) => `${entry.percentage}%`}
                      >
                        {chartData.browsers.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1612', border: '1px solid #cab29750', borderRadius: '8px' }}
                      />
                    </RechartsPie>
                  </ResponsiveContainer>
                  <div className="flex flex-col gap-1 mt-2">
                    {chartData.browsers.map((browser, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx] }}></div>
                        <span className="text-coffee-300">{browser.name}: {browser.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Peak Hours & Top Pages */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Peak Traffic Hours */}
          <div className="bg-glass-dark rounded-2xl p-6 border border-glass-border">
            <h2 className="text-xl font-semibold text-cream-100 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Peak Traffic Hours
            </h2>
            {chartData?.peakHours && (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData.peakHours}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                  <XAxis dataKey="hour" stroke="#cab297" style={{ fontSize: '12px' }} />
                  <YAxis stroke="#cab297" style={{ fontSize: '12px' }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1612', border: '1px solid #cab29750', borderRadius: '8px' }}
                    labelStyle={{ color: '#ffe8c8' }}
                  />
                  <Bar dataKey="requests" fill="#edc58f" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Top Pages */}
          <div className="bg-glass-dark rounded-2xl p-6 border border-glass-border">
            <h2 className="text-xl font-semibold text-cream-100 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Top Sections
            </h2>
            <div className="space-y-3">
              {stats.topSections?.map((section, idx) => (
                <div key={idx}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-cream-200 text-sm font-medium">{section.name}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-coffee-300 text-sm">{section.views} views</span>
                      <span className="text-coffee-400 text-xs">{section.percentage}%</span>
                    </div>
                  </div>
                  <div className="h-2 bg-espresso-900 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-coffee-500 to-caramel-500 transition-all duration-500"
                      style={{ width: `${section.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Live Sessions & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Live Sessions */}
          <div className="bg-glass-dark rounded-2xl p-6 border border-glass-border">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-cream-100 flex items-center gap-2">
                <Users className="w-5 h-5 text-green-500" />
                Live Sessions ({liveSessions.length})
              </h2>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                <span className="text-xs text-coffee-300">Real-time</span>
              </div>
            </div>
            
            <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar">
              {liveSessions.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-coffee-400 mx-auto mb-3 opacity-50" />
                  <p className="text-coffee-400">No active sessions</p>
                </div>
              ) : (
                liveSessions.map((session, idx) => (
                  <div key={idx} className="bg-espresso-900 rounded-lg p-4 border border-glass-border hover:border-coffee-500/50 transition-all">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {session.device === 'mobile' ? (
                          <Smartphone className="w-5 h-5 text-coffee-400" />
                        ) : (
                          <Monitor className="w-5 h-5 text-coffee-400" />
                        )}
                        <div>
                          <p className="text-cream-100 font-medium text-sm">{session.location || 'Unknown Location'}</p>
                          <p className="text-xs text-coffee-400 font-mono">{session.sessionId.substring(0, 16)}...</p>
                        </div>
                      </div>
                      <span className="text-xs text-coffee-300 bg-espresso-950 px-2 py-1 rounded">{formatTimeAgo(session.startTime)}</span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div className="bg-espresso-950 rounded p-2">
                        <span className="text-coffee-400 block mb-1">Duration</span>
                        <span className="text-cream-200 font-semibold">{formatDuration(session.duration)}</span>
                      </div>
                      <div className="bg-espresso-950 rounded p-2">
                        <span className="text-coffee-400 block mb-1">Page</span>
                        <span className="text-cream-200 font-semibold">{session.currentPage}</span>
                      </div>
                      <div className="bg-espresso-950 rounded p-2">
                        <span className="text-coffee-400 block mb-1">Actions</span>
                        <span className="text-cream-200 font-semibold">{session.interactions}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent Events */}
          <div className="bg-glass-dark rounded-2xl p-6 border border-glass-border">
            <h2 className="text-xl font-semibold text-cream-100 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Recent Activity
            </h2>
            
            <div className="space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar">
              {recentEvents.map((event, idx) => (
                <div key={idx} className="bg-espresso-900 rounded-lg p-3 border border-glass-border hover:border-coffee-500/50 transition-all">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getEventIcon(event.eventType)}
                      <div>
                        <span className="text-cream-200 text-sm font-medium">{formatEventType(event.eventType)}</span>
                        {event.data && (
                          <p className="text-xs text-coffee-400 mt-0.5">
                            {event.data}
                          </p>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-coffee-400 bg-espresso-950 px-2 py-1 rounded">{formatTimeAgo(event.timestamp)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Geographic Distribution */}
          <div className="bg-glass-dark rounded-2xl p-6 border border-glass-border">
            <h2 className="text-lg font-semibold text-cream-100 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Top Locations
            </h2>
            <div className="space-y-3">
              {stats.countries?.map((country, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-espresso-900 flex items-center justify-center text-lg">
                      {country.flag || '🌍'}
                    </div>
                    <span className="text-cream-200 text-sm">{country.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-coffee-300 text-sm">{country.count}</span>
                    <div className="w-16 h-1.5 bg-espresso-900 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-coffee-500"
                        style={{ width: `${(country.count / stats.totalVisitors) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Session Duration Distribution */}
          <div className="bg-glass-dark rounded-2xl p-6 border border-glass-border">
            <h2 className="text-lg font-semibold text-cream-100 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Session Duration
            </h2>
            <div className="space-y-3">
              {stats.sessionDuration?.map((range, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-cream-200 text-sm">{range.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-coffee-300 text-sm">{range.count}</span>
                    <div className="w-16 h-1.5 bg-espresso-900 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500"
                        style={{ width: `${range.percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Engagement Metrics */}
          <div className="bg-glass-dark rounded-2xl p-6 border border-glass-border">
            <h2 className="text-lg font-semibold text-cream-100 mb-4 flex items-center gap-2">
              <MousePointer className="w-5 h-5" />
              Engagement
            </h2>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-coffee-300 text-sm">Avg. Clicks per Session</span>
                  <span className="text-cream-100 text-lg font-bold">{stats.avgClicksPerSession || 0}</span>
                </div>
                <div className="h-1.5 bg-espresso-900 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500" style={{ width: `${Math.min((stats.avgClicksPerSession || 0) * 10, 100)}%` }} />
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-coffee-300 text-sm">Scroll Completion</span>
                  <span className="text-cream-100 text-lg font-bold">{stats.avgScrollDepth || 0}%</span>
                </div>
                <div className="h-1.5 bg-espresso-900 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500" style={{ width: `${stats.avgScrollDepth || 0}%` }} />
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-coffee-300 text-sm">Return Visitors</span>
                  <span className="text-cream-100 text-lg font-bold">{stats.returnVisitorRate || 0}%</span>
                </div>
                <div className="h-1.5 bg-espresso-900 rounded-full overflow-hidden">
                  <div className="h-full bg-orange-500" style={{ width: `${stats.returnVisitorRate || 0}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Metric Card with comparison
const MetricCard = ({ icon, title, value, subtitle, change, comparison, color, pulse, inverse }) => {
  const colors = {
    blue: 'from-blue-500/20 to-blue-600/20 border-blue-500/30',
    green: 'from-green-500/20 to-green-600/20 border-green-500/30',
    purple: 'from-purple-500/20 to-purple-600/20 border-purple-500/30',
    orange: 'from-orange-500/20 to-orange-600/20 border-orange-500/30'
  };

  const getChangeColor = () => {
    if (inverse) {
      return change < 0 ? 'text-green-400' : 'text-red-400';
    }
    return change >= 0 ? 'text-green-400' : 'text-red-400';
  };

  const getChangeIcon = () => {
    if (inverse) {
      return change < 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />;
    }
    return change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />;
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-2xl p-6 border relative overflow-hidden`}>
      {pulse && (
        <div className="absolute top-0 right-0 w-3 h-3 m-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
        </div>
      )}
      
      <div className="flex items-center justify-between mb-3">
        <div className="text-cream-100">{icon}</div>
        {change !== undefined && (
          <div className={`flex items-center gap-1 text-xs font-semibold ${getChangeColor()}`}>
            {getChangeIcon()}
            {Math.abs(change)}%
          </div>
        )}
      </div>
      
      <h3 className="text-coffee-300 text-sm mb-1">{title}</h3>
      <p className="text-2xl font-bold text-cream-100 mb-1">{value}</p>
      
      {subtitle && (
        <p className="text-xs text-coffee-400">{subtitle}</p>
      )}
      
      {comparison && (
        <p className="text-xs text-coffee-400 mt-1">
          vs. previous: {comparison}
        </p>
      )}
    </div>
  );
};

const getEventIcon = (eventType) => {
  const icons = {
    click: <MousePointer className="w-4 h-4 text-blue-400" />,
    scroll_milestone: <Activity className="w-4 h-4 text-green-400" />,
    section_view: <Eye className="w-4 h-4 text-purple-400" />,
    session_start: <Users className="w-4 h-4 text-green-400" />,
    session_end: <Users className="w-4 h-4 text-red-400" />,
    time_update: <Clock className="w-4 h-4 text-coffee-400" />,
  };
  return icons[eventType] || <Activity className="w-4 h-4 text-coffee-400" />;
};

const formatEventType = (type) => {
  return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const formatDuration = (ms) => {
  if (!ms) return '0s';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

const formatTimeAgo = (timestamp) => {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

export default Admin;
